import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import SEO from "components/SEO";
import axios from "axios";
import { trackAnalyticsEvent } from "api/analyticsApi";
import {
  Camera,
  CheckCircle,
  FileText,
  Square,
  Star,
  Trash2,
  Upload,
  User,
  Video,
} from "lucide-react";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ;

const isValidObjectId = (value) => /^[a-f\d]{24}$/i.test(String(value || ""));

const getSubmissionErrorMessage = (err, type = "testimonial") => {
  const code = err?.response?.data?.code;
  const serverMessage = err?.response?.data?.error || err?.response?.data?.message;

  if (code === "SPACE_LOCKED_BY_PLAN") {
    return "This testimonial page is temporarily locked because the owner has reached their current plan limit.";
  }

  if (code === "SPACE_ARCHIVED") {
    return "This testimonial page is archived and cannot accept new testimonials.";
  }

  if (code === "TEXT_CREDITS_EXHAUSTED") {
    return "This page has reached its text testimonial limit. Please contact the owner.";
  }

  if (code === "VIDEO_CREDITS_EXHAUSTED") {
    return "This page has reached its video testimonial limit. Please contact the owner.";
  }

  return serverMessage || `Failed to submit ${type}.`;
};


const getSupportedMimeType = () => {
  const types = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4",
  ];

  if (!window.MediaRecorder) return "";

  return types.find((type) => MediaRecorder.isTypeSupported(type)) || "";
};

const getFileExtensionFromMime = (mimeType = "") => {
  if (mimeType.includes("mp4")) return "mp4";
  if (mimeType.includes("webm")) return "webm";
  return "webm";
};

export default function PublicReviewPage({
  embedMode = false,
  spaceOverride = null,
}) {
  const { spaceSlug } = useParams();

  const previewVideoRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const pageViewTrackedRef = useRef(false);

  const [space, setSpace] = useState(spaceOverride);
  const [activeTab, setActiveTab] = useState("text");
  const [rating, setRating] = useState(5);

  const [textForm, setTextForm] = useState({
    name: "",
    email: "",
    role: "",
    testimonialText: "",
    photo: null,
    consent: false,
  });

  const [videoForm, setVideoForm] = useState({
    name: "",
    email: "",
    video: null,
    consent: false,
  });

  const [videoSource, setVideoSource] = useState("record"); // record | upload
  const [recordingStatus, setRecordingStatus] = useState("idle"); // idle | ready | recording | recorded
  const [videoPreviewUrl, setVideoPreviewUrl] = useState("");
  const [recordingError, setRecordingError] = useState("");

  const [loading, setLoading] = useState(!spaceOverride);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const title = useMemo(() => {
    return (
      space?.headerTitle ||
      `Share your experience with ${space?.name || "us"}`
    );
  }, [space]);

  useEffect(() => {
    if (!space?._id || pageViewTrackedRef.current) return;

    pageViewTrackedRef.current = true;
    trackAnalyticsEvent({
      eventType: "public_review_page_view",
      spaceId: space._id,
      spaceSlug: space.slug || spaceSlug,
      source: embedMode ? "collect_embed" : "public_review_page",
      metadata: { embedMode: !!embedMode },
    });
  }, [space?._id, space?.slug, spaceSlug, embedMode]);

  const stopCameraStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (previewVideoRef.current) {
      previewVideoRef.current.srcObject = null;
    }
  };

  const revokeVideoPreview = () => {
    if (videoPreviewUrl) {
      URL.revokeObjectURL(videoPreviewUrl);
    }
  };

  const clearVideoPreview = () => {
    revokeVideoPreview();

    setVideoPreviewUrl("");
    setVideoForm((prev) => ({
      ...prev,
      video: null,
    }));

    chunksRef.current = [];
    setRecordingStatus("idle");
  };

  const handleUploadedVideoPreview = (file) => {
    clearVideoPreview();

    if (!file) return;

    const url = URL.createObjectURL(file);

    setVideoForm((prev) => ({
      ...prev,
      video: file,
    }));

    setVideoPreviewUrl(url);
    setRecordingStatus("recorded");
  };

  useEffect(() => {
    if (spaceOverride) {
      setSpace(spaceOverride);
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function loadSpace() {
      try {
        setLoading(true);
        setError("");

        const res = await axios.get(`${API_BASE}/api/spaces/slug/${spaceSlug}`);

        if (!cancelled) {
          setSpace(res.data);
        }
      } catch (err) {
        console.error(
          "Failed to load public review page:",
          err.response?.data || err
        );

        if (!cancelled) {
          setError("This testimonial page is not available.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadSpace();

    return () => {
      cancelled = true;
    };
  }, [spaceSlug, spaceOverride]);

  useEffect(() => {
    return () => {
      stopCameraStream();

      if (videoPreviewUrl) {
        URL.revokeObjectURL(videoPreviewUrl);
      }
    };
  }, [videoPreviewUrl]);

  const resetTextForm = () => {
    setTextForm({
      name: "",
      email: "",
      role: "",
      testimonialText: "",
      photo: null,
      consent: false,
    });

    setRating(5);
  };

  const resetVideoForm = () => {
    stopCameraStream();
    clearVideoPreview();

    setVideoForm({
      name: "",
      email: "",
      video: null,
      consent: false,
    });

    setVideoSource("record");
    setRecordingStatus("idle");
    setRecordingError("");
  };

  const prepareRecording = async () => {
    try {
      setError("");
      setSuccess("");
      setRecordingError("");

      clearVideoPreview();
      stopCameraStream();

      if (!navigator.mediaDevices?.getUserMedia) {
        setRecordingError("Video recording is not supported in this browser.");
        setVideoSource("upload");
        return;
      }

      if (!window.MediaRecorder) {
        setRecordingError("MediaRecorder is not supported in this browser.");
        setVideoSource("upload");
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      streamRef.current = stream;

      setTimeout(async () => {
        if (previewVideoRef.current) {
          previewVideoRef.current.srcObject = stream;
          previewVideoRef.current.muted = true;
          previewVideoRef.current.playsInline = true;

          try {
            await previewVideoRef.current.play();
          } catch (playErr) {
            console.warn("Preview autoplay blocked:", playErr);
          }
        }
      }, 100);

      setRecordingStatus("ready");
    } catch (err) {
      console.error("Camera permission failed:", err);

      setRecordingError(
        "Camera/microphone permission denied or unavailable. Please use upload video option."
      );

      setVideoSource("upload");
    }
  };

  const startRecording = async () => {
    try {
      setRecordingError("");
      setError("");
      setSuccess("");

      if (!streamRef.current) {
        await prepareRecording();
      }

      if (!streamRef.current) {
        setRecordingError("Camera is not ready. Please enable camera again.");
        return;
      }

      chunksRef.current = [];

      const mimeType = getSupportedMimeType();

      const recorder = mimeType
        ? new MediaRecorder(streamRef.current, { mimeType })
        : new MediaRecorder(streamRef.current);

      recorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const finalMimeType = recorder.mimeType || "video/webm";

        if (!chunksRef.current.length) {
          setRecordingError("Recording failed. No video data was captured.");
          setRecordingStatus("idle");
          stopCameraStream();
          return;
        }

        const blob = new Blob(chunksRef.current, {
          type: finalMimeType,
        });

        if (!blob.size) {
          setRecordingError("Recording failed. Empty video file created.");
          setRecordingStatus("idle");
          stopCameraStream();
          return;
        }

        const extension = getFileExtensionFromMime(finalMimeType);

        const file = new File(
          [blob],
          `video-testimonial-${Date.now()}.${extension}`,
          {
            type: finalMimeType,
          }
        );

        const url = URL.createObjectURL(blob);

        setVideoForm((prev) => ({
          ...prev,
          video: file,
        }));

        setVideoPreviewUrl(url);
        setRecordingStatus("recorded");

        stopCameraStream();
      };

      recorder.start(1000);
      setRecordingStatus("recording");
    } catch (err) {
      console.error("Recording start failed:", err);
      setRecordingError("Unable to start recording. Please try upload instead.");
      setVideoSource("upload");
    }
  };

  const stopRecording = () => {
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
    } catch (err) {
      console.error("Stop recording failed:", err);
      setRecordingError("Unable to stop recording.");
    }
  };

  const submitTextReview = async (e) => {
    e.preventDefault();

    if (!space?._id || !isValidObjectId(space._id)) {
      setError("Space is not loaded correctly. Please refresh and try again.");
      return;
    }

    if (!textForm.testimonialText.trim()) {
      setError("Please enter your feedback.");
      return;
    }

    if (!textForm.consent) {
      setError("Please accept the consent checkbox before submitting.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");

      const formData = new FormData();
      formData.append("spaceId", space._id);
      formData.append("name", textForm.name.trim() || "Anonymous");
      formData.append("email", textForm.email.trim());
      formData.append("role", textForm.role.trim());
      formData.append("stars", String(rating));
      formData.append("testimonialText", textForm.testimonialText.trim());
      formData.append("consent", "true");

      if (textForm.photo) {
        formData.append("photo", textForm.photo);
      }

      await axios.post(`${API_BASE}/api/testimonials/text`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      resetTextForm();
      setSuccess("Thank you! Your text testimonial has been submitted.");
      trackAnalyticsEvent({
        eventType: "text_testimonial_submitted",
        spaceId: space._id,
        spaceSlug: space.slug || spaceSlug,
        source: embedMode ? "collect_embed" : "public_review_page",
        metadata: { rating },
      });
    } catch (err) {
      console.error("Text review submit failed:", err.response?.data || err);

      setError(getSubmissionErrorMessage(err, "testimonial"));
    } finally {
      setSubmitting(false);
    }
  };

  const submitVideoReview = async (e) => {
    e.preventDefault();

    if (!space?._id || !isValidObjectId(space._id)) {
      setError("Space is not loaded correctly. Please refresh and try again.");
      return;
    }

    if (!videoForm.video) {
      setError("Please record or upload a video testimonial.");
      return;
    }

    if (!videoForm.consent) {
      setError("Please accept the consent checkbox before submitting.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");

      const formData = new FormData();
      formData.append("spaceId", space._id);
      formData.append("name", videoForm.name.trim() || "Anonymous");
      formData.append("email", videoForm.email.trim());
      formData.append("consent", "true");
      formData.append("video", videoForm.video);

      await axios.post(`${API_BASE}/api/testimonials/video`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      resetVideoForm();
      setSuccess("Thank you! Your video testimonial has been submitted.");
      trackAnalyticsEvent({
        eventType: "video_testimonial_submitted",
        spaceId: space._id,
        spaceSlug: space.slug || spaceSlug,
        source: embedMode ? "collect_embed" : "public_review_page",
      });
    } catch (err) {
      console.error("Video review submit failed:", err.response?.data || err);

      setError(getSubmissionErrorMessage(err, "video testimonial"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-xl border shadow-sm px-6 py-4">
          Loading testimonial page...
        </div>
      </div>
    );
  }

  if (error && !space) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="bg-white rounded-xl border shadow-sm p-6 max-w-md text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">
            Page unavailable
          </h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <SEO
  title="Submit Your Review - TestimonialsHub"
  description="Share your experience by submitting a text or video testimonial quickly and easily using TestimonialsHub review forms."
  keywords="submit testimonial, leave customer review, video testimonial form, public review page"
  path="/review"
/>

    <div
      className={
        embedMode
          ? "w-full bg-gray-50"
          : "min-h-screen bg-gradient-to-br from-indigo-50 via-white to-pink-50"
      }
    >
      <div className={embedMode ? "w-full" : "max-w-3xl mx-auto px-4 py-10"}>
        <div className="bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden">
          <div
            className="p-6 text-white"
            style={{
              background:
                space?.theme ||
                "linear-gradient(135deg, #4f46e5 0%, #ec4899 100%)",
            }}
          >
            <div className="flex items-center gap-4">
              {space?.logoUrl ? (
                <img
                  src={space.logoUrl}
                  alt={space.name}
                  className="w-14 h-14 rounded-xl bg-white object-cover"
                />
              ) : (
                <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                  <User className="w-7 h-7" />
                </div>
              )}

              <div>
                <p className="text-sm opacity-90">{space?.name}</p>
                <h1 className="text-2xl font-bold">{title}</h1>
              </div>
            </div>

            {space?.customMessage && (
              <p className="mt-4 text-white/90">{space.customMessage}</p>
            )}
          </div>

          <div className="p-6">
            <div className="grid grid-cols-2 gap-3 mb-6">
              <button
                type="button"
                onClick={() => {
                  setActiveTab("text");
                  setError("");
                  setSuccess("");
                  stopCameraStream();
                }}
                className={`flex items-center justify-center gap-2 rounded-xl border px-4 py-3 font-semibold transition ${
                  activeTab === "text"
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FileText className="w-5 h-5" />
                Text Review
              </button>

              <button
                type="button"
                onClick={() => {
                  setActiveTab("video");
                  setError("");
                  setSuccess("");
                }}
                className={`flex items-center justify-center gap-2 rounded-xl border px-4 py-3 font-semibold transition ${
                  activeTab === "video"
                    ? "bg-pink-600 text-white border-pink-600"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Video className="w-5 h-5" />
                Video Review
              </button>
            </div>

            {success && (
              <div className="mb-5 flex items-start gap-3 bg-green-50 border border-green-200 text-green-700 rounded-xl p-4">
                <CheckCircle className="w-5 h-5 mt-0.5" />
                <p className="text-sm">{success}</p>
              </div>
            )}

            {error && (
              <div className="mb-5 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">
                {error}
              </div>
            )}

            {activeTab === "text" ? (
              <form onSubmit={submitTextReview} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Name <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={textForm.name}
                      onChange={(e) =>
                        setTextForm((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      placeholder="Anonymous"
                      className="w-full border rounded-xl px-4 py-3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Email <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="email"
                      value={textForm.email}
                      onChange={(e) =>
                        setTextForm((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      placeholder="you@example.com"
                      className="w-full border rounded-xl px-4 py-3"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Designation / Company{" "}
                    <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={textForm.role}
                    onChange={(e) =>
                      setTextForm((prev) => ({
                        ...prev,
                        role: e.target.value,
                      }))
                    }
                    placeholder="Founder, Marketing Manager, Customer..."
                    className="w-full border rounded-xl px-4 py-3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Rating
                  </label>

                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((item) => (
                      <button
                        key={item}
                        type="button"
                        onClick={() => setRating(item)}
                        className="p-1"
                      >
                        <Star
                          className={`w-7 h-7 ${
                            item <= rating
                              ? "fill-yellow-400 text-yellow-400"
                              : "text-gray-300"
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Your Feedback
                  </label>
                  <textarea
                    value={textForm.testimonialText}
                    onChange={(e) =>
                      setTextForm((prev) => ({
                        ...prev,
                        testimonialText: e.target.value,
                      }))
                    }
                    placeholder="Write your review here..."
                    rows={5}
                    className="w-full border rounded-xl px-4 py-3 resize-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Profile Photo{" "}
                    <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      setTextForm((prev) => ({
                        ...prev,
                        photo: e.target.files?.[0] || null,
                      }))
                    }
                    className="w-full border rounded-xl px-4 py-3"
                  />
                </div>

                <label className="flex gap-3 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={textForm.consent}
                    onChange={(e) =>
                      setTextForm((prev) => ({
                        ...prev,
                        consent: e.target.checked,
                      }))
                    }
                    className="mt-1"
                  />
                  <span>
                    I give permission to use this testimonial publicly.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-indigo-600 text-white rounded-xl py-3 font-semibold hover:bg-indigo-700 disabled:opacity-60"
                >
                  {submitting ? "Submitting..." : "Submit Text Review"}
                </button>
              </form>
            ) : (
              <form onSubmit={submitVideoReview} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Name <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={videoForm.name}
                      onChange={(e) =>
                        setVideoForm((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      placeholder="Anonymous"
                      className="w-full border rounded-xl px-4 py-3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Email <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="email"
                      value={videoForm.email}
                      onChange={(e) =>
                        setVideoForm((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      placeholder="you@example.com"
                      className="w-full border rounded-xl px-4 py-3"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setVideoSource("record");
                      setError("");
                      setSuccess("");
                      clearVideoPreview();
                      stopCameraStream();
                      setRecordingStatus("idle");
                    }}
                    className={`rounded-xl border px-4 py-3 font-semibold ${
                      videoSource === "record"
                        ? "bg-pink-600 text-white border-pink-600"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Record Video
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setVideoSource("upload");
                      setError("");
                      setSuccess("");
                      stopCameraStream();
                      clearVideoPreview();
                      setRecordingStatus("idle");
                    }}
                    className={`rounded-xl border px-4 py-3 font-semibold ${
                      videoSource === "upload"
                        ? "bg-pink-600 text-white border-pink-600"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Upload Video
                  </button>
                </div>

                {videoSource === "record" ? (
                  <div className="border rounded-2xl p-4 bg-gray-50">
                    <div className="bg-black rounded-xl overflow-hidden mb-4 min-h-[220px] flex items-center justify-center">
                      {videoPreviewUrl ? (
                        <video
                          key={videoPreviewUrl}
                          src={videoPreviewUrl}
                          controls
                          playsInline
                          preload="metadata"
                          className="w-full max-h-80 bg-black"
                          onLoadedMetadata={(e) => {
                            e.currentTarget.currentTime = 0;
                          }}
                        />
                      ) : (
                        <video
                          ref={previewVideoRef}
                          autoPlay
                          playsInline
                          muted
                          className="w-full max-h-80 bg-black"
                        />
                      )}
                    </div>

                    {recordingError && (
                      <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">
                        {recordingError}
                      </div>
                    )}

                    <div className="flex flex-wrap gap-3">
                      {recordingStatus === "idle" && (
                        <button
                          type="button"
                          onClick={prepareRecording}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-black"
                        >
                          <Camera className="w-4 h-4" />
                          Enable Camera
                        </button>
                      )}

                      {recordingStatus === "ready" && (
                        <button
                          type="button"
                          onClick={startRecording}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
                        >
                          <Camera className="w-4 h-4" />
                          Start Recording
                        </button>
                      )}

                      {recordingStatus === "recording" && (
                        <button
                          type="button"
                          onClick={stopRecording}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-black"
                        >
                          <Square className="w-4 h-4" />
                          Stop Recording
                        </button>
                      )}

                      {recordingStatus === "recorded" && (
                        <>
                          <button
                            type="button"
                            onClick={prepareRecording}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-pink-600 text-white hover:bg-pink-700"
                          >
                            <Camera className="w-4 h-4" />
                            Re-record
                          </button>

                          <button
                            type="button"
                            onClick={clearVideoPreview}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-white"
                          >
                            <Trash2 className="w-4 h-4" />
                            Remove
                          </button>
                        </>
                      )}
                    </div>

                    {recordingStatus === "recorded" && videoForm.video && (
                      <p className="text-xs text-green-600 mt-3">
                        Video recorded successfully. Preview it above, then
                        submit.
                      </p>
                    )}

                    <p className="text-xs text-gray-500 mt-3">
                      Camera recording works on localhost and HTTPS domains only.
                    </p>
                  </div>
                ) : (
                  <div className="border-2 border-dashed rounded-2xl p-6 text-center bg-gray-50">
                    <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />

                    <p className="font-medium text-gray-700 mb-2">
                      Upload your video testimonial
                    </p>

                    <input
                      type="file"
                      accept="video/*"
                      onChange={(e) =>
                        handleUploadedVideoPreview(e.target.files?.[0] || null)
                      }
                      className="w-full border rounded-xl px-4 py-3 bg-white"
                    />

                    {videoPreviewUrl && (
                      <div className="mt-5">
                        <p className="text-sm font-medium text-gray-700 mb-2">
                          Uploaded Video Preview
                        </p>

                        <video
                          key={videoPreviewUrl}
                          src={videoPreviewUrl}
                          controls
                          playsInline
                          preload="metadata"
                          className="w-full max-h-80 rounded-xl bg-black"
                          onLoadedMetadata={(e) => {
                            e.currentTarget.currentTime = 0;
                          }}
                        />

                        <button
                          type="button"
                          onClick={clearVideoPreview}
                          className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-white"
                        >
                          <Trash2 className="w-4 h-4" />
                          Remove Video
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <label className="flex gap-3 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={videoForm.consent}
                    onChange={(e) =>
                      setVideoForm((prev) => ({
                        ...prev,
                        consent: e.target.checked,
                      }))
                    }
                    className="mt-1"
                  />
                  <span>
                    I give permission to use this testimonial publicly.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-pink-600 text-white rounded-xl py-3 font-semibold hover:bg-pink-700 disabled:opacity-60"
                >
                  {submitting ? "Uploading..." : "Submit Video Review"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
